import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Spinner } from '../ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import BrandSelect from '../ui/BrandSelect';
import { mappingRowSelect } from '../utils/ReactSelectStyles';
import WorkItemConversationTab from './WorkItemConversationTab';
import WorkItemTimeTab from './WorkItemTimeTab';
import WorkItemActivityTab from './WorkItemActivityTab';
import WorkItemFilesTab from './WorkItemFilesTab';
import {
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  dueUrgency,
  itemTimeMinutes,
  itemMessageCount,
  isChaseable,
} from './workItemQueueFilters';

/**
 * Non-modal right rail detail panel with tabbed sections.
 */
const WorkItemDetailPanel = ({
  open,
  detail,
  detailLoading,
  assigneeOptions,
  projects,
  projectName,
  setProjectName,
  onClose,
  onSaveField,
  onDetailChange,
  onItemUpdated,
  listProjects,
  createProject,
  setProjects,
  postWorkItemMessage,
  requestAttachmentUpload,
  addWorkItemFiles,
  downloadWorkItemAttachment,
  removeWorkItemAttachment,
  renameWorkItemAttachment,
  createChaseDraft,
  approveChaseDraft,
  addTimeEntry,
  updateTimeEntry,
  showAlert,
  panelTab,
  setPanelTab,
  resolveName,
  resolveProjectName,
  profileSub,
  onProjectCreated,
}) => {
  const [assigneeFocus, setAssigneeFocus] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const due = useMemo(() => dueUrgency(detail?.dueDate), [detail?.dueDate]);
  const mins = detail ? itemTimeMinutes(detail) : 0;
  const msgs = detail ? itemMessageCount(detail) : 0;
  const chasePending = detail
    ? (detail.chasePendingCount || 0) > 0 ||
      (detail.draftMessages || []).some((d) => d.status === 'pending_approval')
    : false;

  const nextAction = useMemo(() => {
    if (!detail) return null;
    if (!detail.assigneeUserId) {
      return {
        label: 'Assign someone',
        run: () => {
          setPanelTab('details');
          setAssigneeFocus(true);
        },
      };
    }
    if (detail.status === 'Marked complete') {
      return {
        label: 'Confirm Done',
        run: () => onSaveField({ status: 'Done' }),
      };
    }
    if (detail.taskHolder === 'responding' || detail.status === 'Waiting on client') {
      if (!detail.shared) {
        return {
          label: 'Share with client',
          run: () => onSaveField({ shared: true }),
        };
      }
      if (isChaseable(detail)) {
        return {
          label: 'Draft chase',
          run: () => setPanelTab('conversation'),
        };
      }
    }
    return null;
  }, [detail, onSaveField, setPanelTab]);

  if (!open) return null;

  return (
    <aside
      className='fixed inset-y-0 right-0 z-40 flex w-full max-w-xl flex-col border-l border-border bg-card shadow-none'
      style={{ width: 'min(100vw, 34rem)' }}
      aria-label='Work item detail'>
      <div className='flex items-start justify-between gap-2 border-b border-border px-4 py-3 shrink-0'>
        <div className='min-w-0 flex-1 space-y-1'>
          {detailLoading && !detail ? (
            <Spinner />
          ) : (
            <>
              <h2 className='text-base font-semibold text-foreground truncate'>
                {detail?.title || 'Work item'}
              </h2>
              <div className='flex flex-wrap items-center gap-1.5 text-xs'>
                {detail?.status ? <Badge variant='outline'>{detail.status}</Badge> : null}
                <span
                  className={
                    due.tone === 'overdue'
                      ? 'text-destructive'
                      : due.tone === 'today'
                        ? 'text-warning'
                        : 'text-muted-foreground'
                  }>
                  {due.label}
                </span>
                <span className='text-muted-foreground tabular-nums'>{mins}m</span>
                <span className='text-muted-foreground'>{msgs} msg</span>
                {detail?.shared ? <Badge variant='outline'>Shared</Badge> : null}
                {chasePending ? (
                  <Badge variant='outline' className='text-warning'>
                    Chase pending
                  </Badge>
                ) : null}
              </div>
            </>
          )}
        </div>
        <Button variant='ghost' size='icon' onClick={onClose} aria-label='Close detail'>
          <X size={16} strokeWidth={1.5} />
        </Button>
      </div>

      <div className='flex-1 overflow-y-auto px-4 py-3'>
        {detailLoading && !detail ? (
          <div className='flex justify-center py-8'>
            <Spinner />
          </div>
        ) : null}
        {detail && (
          <Tabs value={panelTab} onValueChange={setPanelTab}>
            <TabsList className='mb-3 w-full justify-start overflow-x-auto'>
              <TabsTrigger value='details' className='px-3 py-2 text-xs'>
                Details
              </TabsTrigger>
              <TabsTrigger value='conversation' className='px-3 py-2 text-xs'>
                Conversation
              </TabsTrigger>
              <TabsTrigger value='files' className='px-3 py-2 text-xs'>
                Files
              </TabsTrigger>
              <TabsTrigger value='time' className='px-3 py-2 text-xs'>
                Time
              </TabsTrigger>
              <TabsTrigger value='activity' className='px-3 py-2 text-xs'>
                Activity
              </TabsTrigger>
            </TabsList>

            <TabsContent value='details' className='space-y-3 mt-0'>
              {nextAction ? (
                <Button size='sm' variant='amber' onClick={nextAction.run}>
                  {nextAction.label}
                </Button>
              ) : null}
              <Input
                value={detail.title || ''}
                onChange={(e) => onDetailChange({ ...detail, title: e.target.value })}
                onBlur={() => onSaveField({ title: detail.title })}
              />
              <textarea
                className='w-full min-h-[72px] rounded-md border border-border bg-input px-3 py-2 text-sm'
                value={detail.description || ''}
                placeholder='Description'
                onChange={(e) => onDetailChange({ ...detail, description: e.target.value })}
                onBlur={() => onSaveField({ description: detail.description })}
              />
              <div className='grid grid-cols-2 gap-2'>
                <div>
                  <label className='text-xs text-muted-foreground'>Status</label>
                    <BrandSelect
                    styles={mappingRowSelect}
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find((o) => o.value === detail.status) || null}
                    onChange={(opt) => opt && onSaveField({ status: opt.value })}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  />
                </div>
                <div>
                  <label className='text-xs text-muted-foreground'>Priority</label>
                  <BrandSelect
                    styles={mappingRowSelect}
                    options={PRIORITY_OPTIONS}
                    value={PRIORITY_OPTIONS.find((o) => o.value === detail.priority) || PRIORITY_OPTIONS[1]}
                    onChange={(opt) => opt && onSaveField({ priority: opt.value })}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  />
                </div>
                <div>
                  <label className='text-xs text-muted-foreground'>Due date</label>
                  <Input
                    type='date'
                    value={detail.dueDate || ''}
                    onChange={(e) => onSaveField({ dueDate: e.target.value || null })}
                  />
                </div>
                <div>
                  <label className='text-xs text-muted-foreground'>Assignee</label>
                  <BrandSelect
                    styles={mappingRowSelect}
                    options={assigneeOptions}
                    value={
                      assigneeOptions.find((o) => o.value === (detail.assigneeUserId || '')) || null
                    }
                    onChange={(opt) => {
                      setAssigneeFocus(false);
                      onSaveField({
                        assigneeUserId: opt?.value || null,
                        assigneeType:
                          detail.status === 'Waiting on client'
                            ? 'responding_member'
                            : 'requesting_member',
                      });
                    }}
                    placeholder={assigneeFocus ? 'Select assignee…' : 'Select assignee'}
                    isClearable
                    menuIsOpen={assigneeFocus || undefined}
                    onMenuClose={() => setAssigneeFocus(false)}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  />
                </div>
              </div>
              <div className='flex flex-wrap gap-2'>
                <Button
                  size='sm'
                  variant={detail.shared ? 'default' : 'outline'}
                  onClick={() => onSaveField({ shared: !detail.shared })}>
                  {detail.shared ? 'Shared with client' : 'Share with client'}
                </Button>
                <Button size='sm' variant='outline' onClick={() => onSaveField({ billable: !detail.billable })}>
                  Billable: {detail.billable ? 'yes' : 'no'}
                </Button>
                {detail.visibilityState === 'draft' && (
                  <Button size='sm' onClick={() => onSaveField({ visibilityState: 'published' })}>
                    Publish draft
                  </Button>
                )}
              </div>
              <div className='space-y-2 border-t border-border pt-3'>
                <p className='text-xs font-medium text-muted-foreground'>Project</p>
                <BrandSelect
                  styles={mappingRowSelect}
                  isClearable
                  options={(projects || [])
                    .filter((p) => p.status !== 'closed')
                    .map((p) => ({ value: p.id, label: p.name }))}
                  value={
                    projects
                      .map((p) => ({ value: p.id, label: p.name }))
                      .find((o) => o.value === detail.projectId) || null
                  }
                  onChange={(opt) => onSaveField({ projectId: opt?.value || null })}
                  placeholder='No project'
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
                <div className='flex gap-2'>
                  <Input
                    placeholder='New project name'
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                  />
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={async () => {
                      if (!projectName.trim()) return;
                      const result = await createProject({
                        clientOrgId: detail.clientOrgId,
                        name: projectName.trim(),
                        kind: 'other',
                      });
                        if (result?.ok) {
                        setProjectName('');
                        const proj = await listProjects(detail.clientOrgId);
                        if (proj?.ok) setProjects(proj.projects || []);
                        if (result.project && onProjectCreated) onProjectCreated(result.project);
                        await onSaveField({ projectId: result.project.id });
                      } else {
                        showAlert(true, result?.message || 'Project create failed.', 'error', 5000);
                      }
                    }}>
                    Add project
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value='conversation' className='mt-0'>
              <WorkItemConversationTab
                detail={detail}
                postWorkItemMessage={postWorkItemMessage}
                requestAttachmentUpload={requestAttachmentUpload}
                downloadWorkItemAttachment={downloadWorkItemAttachment}
                removeWorkItemAttachment={removeWorkItemAttachment}
                renameWorkItemAttachment={renameWorkItemAttachment}
                createChaseDraft={createChaseDraft}
                approveChaseDraft={approveChaseDraft}
                showAlert={showAlert}
                onItemUpdated={onItemUpdated}
                resolveName={resolveName}
                profileSub={profileSub}
              />
            </TabsContent>

            <TabsContent value='files' className='mt-0'>
              <WorkItemFilesTab
                detail={detail}
                addWorkItemFiles={addWorkItemFiles}
                requestAttachmentUpload={requestAttachmentUpload}
                downloadWorkItemAttachment={downloadWorkItemAttachment}
                removeWorkItemAttachment={removeWorkItemAttachment}
                renameWorkItemAttachment={renameWorkItemAttachment}
                showAlert={showAlert}
                onItemUpdated={onItemUpdated}
                pendingFiles={pendingFiles}
                setPendingFiles={setPendingFiles}
              />
            </TabsContent>

            <TabsContent value='time' className='mt-0'>
              <WorkItemTimeTab
                detail={detail}
                addTimeEntry={addTimeEntry}
                updateTimeEntry={updateTimeEntry}
                showAlert={showAlert}
                onItemUpdated={onItemUpdated}
                resolveName={resolveName}
                profileSub={profileSub}
              />
            </TabsContent>

            <TabsContent value='activity' className='mt-0'>
              <WorkItemActivityTab
                detail={detail}
                resolveName={resolveName}
                resolveProjectName={resolveProjectName}
                profileSub={profileSub}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </aside>
  );
};

export default WorkItemDetailPanel;
